import { useEffect, useMemo, useRef, type ReactNode } from 'react'
import { ROUTES } from '@/lib/routes'
import { useTranslation } from 'react-i18next'
import { AlertCircle, CreditCard, Loader2, Lock, LogIn, UserPlus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { rememberReturnTo } from '@/lib/returnTo'
import { sdk } from '@/lib/sdk'
import { usePurchaseFlow, type CompanyPurchase, type PurchaseFlow } from '@/lib/hooks/usePurchaseFlow'
import { useRoute } from '@/lib/RouteContext'
import { useCommunity } from '@/lib/CommunityContext'
import { useMoney } from '@/lib/hooks/useMoney'
import type { PaymentMethod, PublicProduct } from '@/types/checkout'
import type { FormAnswerInput } from '@/types/forms'
import { PaymentMethodSelector } from './PaymentMethodSelector'
import { CustomerDataFields } from './CustomerDataFields'
import { CardPaymentFields } from './CardPaymentFields'
import { PriceSummary, SummaryAddOn } from './PriceSummary'
import type { CheckoutOutcome } from '@/lib/checkout/outcome'
import { PurchaseOutcomeView } from './PurchaseOutcomeView'
import { LastChanceOffers } from '@/components/features/downsell/LastChanceOffers'
import { CouponField } from './CouponField'
import { ShippingAddressFields } from '@/components/features/products/ShippingAddressFields'
import { JoinWaitlistButton } from '@/components/features/products/JoinWaitlistButton'
import { useMemberEntry } from '@/lib/hooks/useMemberEntry'
import { SuspendedNotice } from '@/components/features/memberEntry/SuspendedNotice'
import { PURCHASE_AGE } from '@/lib/ageGate'

interface CheckoutPanelProps {
  product: PublicProduct
  /** The summary line, e.g. "Gold · Annual" or the product title. */
  lineLabel: string
  /** The submit button, e.g. "Subscribe" or "Buy". */
  submitLabel: string
  /** Called from the confirmation screen; omitted, the screen has no button. */
  onDone?: () => void
  doneLabel?: string
  /** Add-ons bought with the plan, in one order and one charge (F-37). */
  addOns?: SummaryAddOn[]
  /**
   * Once an order exists (PIX, boleto, card answered or processing), hand it
   * over instead of showing the outcome here: the pricing page moves to its
   * own confirmation page, which survives a reload. A refusal stays here.
   */
  onOrderCreated?: (outcome: CheckoutOutcome) => void
  /** F-93 1B: the order is the company's (its admin places it; CNPJ, no trial). */
  company?: CompanyPurchase | null
  /** "See your subscription": `/billing` unless the caller says otherwise (the company's live in "My company"). */
  onSeeSubscription?: () => void
  /** F-116 / F-117 4: an event ticket type bought in quantity, with the registration answers. */
  ticket?: { quantity?: number; registrationAnswers?: FormAnswerInput[] } | null
  className?: string
}

const openLogin = () => {
  rememberReturnTo()
  ;(document.querySelector('[data-login-trigger]') as HTMLButtonElement | null)?.click()
}

/* F-150: the request to join is the Header's signup dialog, in its "ask to join" form. */
const openRequestToJoin = () => {
  rememberReturnTo()
  ;(document.querySelector('[data-signup-trigger]') as HTMLButtonElement | null)?.click()
}

/**
 * The whole payment step for one product: method, buyer, card when it applies,
 * the price as the server computes it, and the outcome.
 *
 * Mountable anywhere — the pricing page, the product catalog dialog, a
 * storefront space — because it takes a product and reports back through
 * `onDone`; it never navigates on its own.
 *
 * The coupon (FIN-7) is sent with every quote and with the checkout; the
 * discount shown is the server's, and the same code goes on the charge.
 */
const NO_ADD_ONS: SummaryAddOn[] = []

export function CheckoutPanel({ product, lineLabel, submitLabel, onDone, doneLabel, addOns = NO_ADD_ONS, onOrderCreated, company = null, onSeeSubscription, ticket = null, className }: CheckoutPanelProps) {
  const { t } = useTranslation(['checkout', 'products', 'companies'])
  const addOnIds = useMemo(() => addOns.map((addOn) => addOn.productId), [addOns])
  const flow = usePurchaseFlow(product, addOnIds, addOns, company, ticket)
  const seeSubscription = onSeeSubscription ?? (() => navigate(ROUTES.accountBilling))
  const { navigate } = useRoute()
  const { suspended: salesClosed } = useMemberEntry()

  /* F-102: the checkout of a paid product opened; the API keeps it as a cart
     and lists it as abandoned if no payment follows within the hour. It
     ignores a free product, one the member already has and a company order.
     Best effort: a failure here never gets in the way of the purchase. */
  const signedIn = flow.isAuthenticated
  const paid = Number(product.price) > 0
  const companyId = company?.id
  useEffect(() => {
    if (!signedIn || !paid) return
    sdk.abandonedCarts.register({ productId: product.id, ...(companyId ? { companyId } : {}) }).catch(() => undefined)
  }, [signedIn, paid, product.id, companyId])

  /* The outcome is shorter than the form it replaces; on a phone the page
     shifts and the PIX code can land outside the screen. Bring it back. */
  const outcomeRef = useRef<HTMLDivElement>(null)
  const outcomeKind = flow.outcome?.kind
  useEffect(() => {
    if (outcomeKind) outcomeRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }, [outcomeKind])

  const handOver = Boolean(onOrderCreated && flow.outcome && flow.outcome.kind !== 'refused' && flow.outcome.purchaseId)
  useEffect(() => {
    if (handOver && flow.outcome) onOrderCreated?.(flow.outcome)
    // Once per outcome.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handOver, flow.outcome])

  if (handOver) {
    return (
      <div className={cn('flex items-center justify-center py-10', className)}>
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    )
  }

  if (flow.outcome) {
    return (
      <div ref={outcomeRef} className={className}>
        <PurchaseOutcomeView
          outcome={flow.outcome}
          onRetry={flow.reset}
          onDone={onDone}
          doneLabel={doneLabel}
          freeText={company ? {
            title: t('companies:purchase.freeDoneTitle', { company: company.name }),
            description: t('companies:purchase.freeDoneDescription')
          } : undefined}
        />
        {/* F-39: once the plan is paid, the add-ons left out get a last chance. */}
        {flow.outcome.kind === 'paid' && !company && (product.type === 'SUBSCRIPTION' || product.billingInterval) && (
          <LastChanceOffers waitForOffers className="mt-6 border-t border-gray-200 pt-5" />
        )}
      </div>
    )
  }

  /* F-86: the SuperAdmin suspended the community; nothing new is sold, not
     even a free product. The API refuses too; this says so before the form. */
  if (salesClosed) {
    return (
      <div className={cn('space-y-2', className)} data-testid="checkout-unavailable">
        <p className="text-base font-semibold text-gray-900">{t('common:memberEntry.unavailable')}</p>
        <SuspendedNotice kind="sale" />
      </div>
    )
  }

  if (!flow.isFree && flow.allowedMethods.length === 0) {
    return (
      <div className={cn('flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-800', className)}>
        <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
        {t('purchase.noMethods')}
      </div>
    )
  }

  return (
    <CheckoutForm
      flow={flow}
      product={product}
      lineLabel={lineLabel}
      submitLabel={submitLabel}
      addOns={addOns}
      company={company}
      onSeeSubscription={seeSubscription}
      className={className}
    />
  )
}

/** What the form reads from a flow: the checkout's own (`usePurchaseFlow`) or another one shaped like it (F-195). */
export type CheckoutFormFlow = Omit<PurchaseFlow, 'product' | 'quote'> & {
  quote: Pick<PurchaseFlow['quote'], 'quote' | 'loading' | 'failed' | 'retry' | 'refusal'>
}

export interface CheckoutFormProps {
  flow: CheckoutFormFlow
  product: PublicProduct
  lineLabel: string
  submitLabel: string
  /** Add-ons in the same order (F-37), one line each in the checkout's own summary. */
  addOns?: SummaryAddOn[]
  company?: CompanyPurchase | null
  onSeeSubscription: () => void
  className?: string
  /**
   * The order's lines and total. `null` (the checkout): the coupon field and `PriceSummary` from the flow's
   * quote. A node: the caller's own lines, with the shared `SummaryRow`/`SummaryTotal`, and no coupon field
   * (the plan change prices from its own quote and takes no coupon).
   */
  summary?: ReactNode | null
  /** Nothing is charged now: no method, buyer or card, only the summary and the button. */
  noPayment?: boolean
  /** In place of the buyer data and the card form, for a card already on file (the plan change's saved card). */
  savedPayment?: ReactNode
  /** Under the card form (e.g. "use the saved card"). */
  cardFooter?: ReactNode
  /** Methods shown but not available yet, each with its reason on hover and under the row. */
  unavailableMethods?: { methods: PaymentMethod[]; hint: string } | null
  /** The installment option labels, when the caller prices them itself. */
  installmentLabel?: (count: number) => string
  /** Warnings right above the button. */
  notices?: ReactNode
  /** The caller has a reason the button waits (it shows that reason in `notices`). */
  submitDisabled?: boolean
  /** The button while it works ("Processing" by default). */
  busyLabel?: string
  testId?: string
}

/**
 * The checkout's form, the one "Payment details" shows: method, buyer, card, the order and the button,
 * with the secure-payment note. `CheckoutPanel` drives it with a purchase; the plan change (F-195) with its
 * own flow and summary, so both look and behave the same.
 */
export function CheckoutForm({
  flow,
  product,
  lineLabel,
  submitLabel,
  addOns = NO_ADD_ONS,
  company = null,
  onSeeSubscription: seeSubscription,
  className,
  summary = null,
  noPayment = false,
  savedPayment,
  cardFooter,
  unavailableMethods = null,
  installmentLabel: installmentLabelProp,
  notices,
  submitDisabled = false,
  busyLabel,
  testId
}: CheckoutFormProps) {
  const { t } = useTranslation(['checkout', 'products', 'companies'])
  const { community } = useCommunity()
  const { navigate } = useRoute()
  const formatMoney = useMoney()
  const asksToJoin = Boolean(community?.signupEnabled && community?.signupRequiresApproval)
  const ownSummary = summary !== null && summary !== undefined
  const paying = !flow.isFree && !noPayment

  const busy = flow.submitting
  /* F-12: the API will not sell this product (e.g. the community cannot
     receive payments yet). Its reason is shown and nothing can be submitted. */
  const refusal = flow.quote.refusal
  const held = Boolean(flow.quote.quote?.alreadySubscribed)
  const cardBlocked = paying && flow.method === 'CREDIT_CARD' && !flow.cardAvailable && !savedPayment

  /* F-43: each count is priced by the API with its own interest
     (`installmentOptions`): "12× de R$ 85,99 (total R$ 1.031,90)" when the
     buyer pays it, "12× de R$ 83,33 sem juros" when the organization does. */
  const options = flow.quote.quote?.installmentOptions
  const organizationPays = flow.quote.quote?.installmentInterestPayer === 'ORGANIZATION'
  const installmentLabel = installmentLabelProp ?? (options?.length
    ? (count: number) => {
        const option = options.find((o) => o.installments === count)
        if (!option) return t('purchase.installmentOption', { count })
        const each = formatMoney(Number(option.installmentAmount), flow.currency)
        if (count === 1) return t('purchase.installmentOptionAmount', { count, amount: each })
        if (organizationPays || Number(option.percent) === 0) return t('purchase.installmentInterestFree', { count, amount: each })
        return t('purchase.installmentWithInterest', { count, amount: each, total: formatMoney(Number(option.totalAmount), flow.currency) })
      }
    : undefined)

  return (
    <form
      noValidate
      className={cn('space-y-5', className)}
      data-testid={testId}
      onSubmit={(e) => {
        e.preventDefault()
        void flow.submit()
      }}
    >
      {paying && (<>
      <section>
        <h4 className="mb-2 text-xs text-gray-500">{t('purchase.paymentMethod')}</h4>
        <PaymentMethodSelector
          methods={unavailableMethods ? [...flow.allowedMethods, ...unavailableMethods.methods.filter((m) => !flow.allowedMethods.includes(m))] : flow.allowedMethods}
          unavailable={unavailableMethods?.methods}
          unavailableHint={unavailableMethods?.hint}
          value={flow.method}
          onChange={flow.setMethod}
          disabled={busy}
        />
        {unavailableMethods && unavailableMethods.methods.length > 0 && (
          <p className="mt-2 text-xs text-gray-500" data-testid="checkout-methods-soon">{unavailableMethods.hint}</p>
        )}
        {flow.method === 'PIX' && <p className="mt-2 text-xs text-gray-500">{t('purchase.pixHint')}</p>}
        {flow.method === 'BOLETO' && <p className="mt-2 text-xs text-gray-500">{t('purchase.boletoHint')}</p>}
        {flow.showInstallmentAmounts && (flow.method === 'PIX' || flow.method === 'BOLETO') && (
          <p className="mt-1 text-xs text-gray-500">{t('purchase.yearlyUpfrontHint')}</p>
        )}
      </section>

      {flow.isAuthenticated && savedPayment && flow.method === 'CREDIT_CARD' && (
        <section>
          <h4 className="mb-2 text-xs text-gray-500">{t('purchase.cardData')}</h4>
          {savedPayment}
        </section>
      )}

      {flow.isAuthenticated && !(savedPayment && flow.method === 'CREDIT_CARD') && (
        <>
          <section>
            <h4 className="mb-2 text-xs text-gray-500">{t(company ? 'purchase.companyData' : 'purchase.buyerData')}</h4>
            <CustomerDataFields
              value={flow.customer}
              errors={flow.fieldErrors}
              onChange={flow.setCustomerField}
              saveDocument={flow.canSaveDocument ? { checked: flow.saveDocument, onChange: flow.setSaveDocument } : null}
              company={Boolean(company)}
              disabled={busy}
            />
          </section>

          {flow.method === 'CREDIT_CARD' && (
            <section>
              <h4 className="mb-2 text-xs text-gray-500">{t('purchase.cardData')}</h4>
              {cardBlocked ? (
                <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800">{t('purchase.errors.cardUnavailable')}</p>
              ) : (
                <CardPaymentFields
                  value={flow.card}
                  errors={flow.fieldErrors}
                  onChange={flow.setCardField}
                  maxInstallments={flow.maxInstallments}
                  installments={flow.installments}
                  onInstallmentsChange={flow.setInstallments}
                  installmentLabel={installmentLabel}
                  disabled={busy}
                />
              )}
              {!cardBlocked && flow.savesCard && (
                <p className="mt-2 flex items-start gap-1.5 text-xs text-gray-500">
                  <CreditCard className="mt-px h-3.5 w-3.5 flex-shrink-0" />
                  {t('purchase.cardSavedForRenewal')}
                </p>
              )}
              {cardFooter && <div className="mt-2">{cardFooter}</div>}
            </section>
          )}
        </>
      )}

      </>)}

      {flow.needsShipping && flow.isAuthenticated && (
        <section data-testid="shipping-section">
          <h4 className="mb-2 text-xs text-gray-500">{t('products:shipping.title')}</h4>
          <ShippingAddressFields
            value={flow.shipping}
            errors={flow.shippingErrors}
            onChange={flow.setShippingField}
            disabled={busy}
          />
        </section>
      )}

      {ownSummary ? summary : flow.isFree ? (
        <>
          {flow.couponZeroed && (
            <CouponField
              applied={flow.appliedCoupon}
              discountAmount={flow.discountAmount}
              currency={flow.currency}
              pending={flow.couponPending}
              error={flow.couponError}
              onApply={flow.applyCoupon}
              onRemove={flow.removeCoupon}
              recurring={product.type === 'SUBSCRIPTION' || Boolean(product.billingInterval)}
              billingInterval={product.billingInterval}
              disabled={busy}
            />
          )}
          <p className="flex items-center justify-between rounded-lg bg-green-50 p-3 text-sm text-green-800" data-testid="free-summary">
            <span className="min-w-0 break-words font-medium">{lineLabel}</span>
            <span className="ml-3 flex-shrink-0 font-semibold">{t('products:free')}</span>
          </p>
        </>
      ) : (<>
      <CouponField
        applied={flow.appliedCoupon}
        discountAmount={flow.discountAmount}
        currency={flow.currency}
        pending={flow.couponPending}
        error={flow.couponError}
        onApply={flow.applyCoupon}
        onRemove={flow.removeCoupon}
        recurring={product.type === 'SUBSCRIPTION' || Boolean(product.billingInterval)}
        billingInterval={product.billingInterval}
        disabled={busy}
      />

      <PriceSummary
        lineLabel={lineLabel}
        listPrice={parseFloat(product.price)}
        quote={flow.quote.quote}
        loading={flow.quote.loading}
        failed={flow.quote.failed}
        onRetry={flow.quote.retry}
        totalAmount={flow.totalAmount}
        currency={flow.currency}
        installments={flow.method === 'CREDIT_CARD' ? flow.installments : 1}
        showInstallmentAmounts={flow.showInstallmentAmounts}
        interest={
          flow.method === 'CREDIT_CARD' && flow.quote.quote?.installmentInterestPayer !== 'ORGANIZATION' && Number(flow.quote.quote?.installmentInterest) > 0
            ? { amount: Number(flow.quote.quote?.installmentInterest), percent: flow.quote.quote?.installmentInterestPercent ?? '' }
            : null
        }
        installmentAmount={flow.quote.quote?.installmentAmount ? Number(flow.quote.quote.installmentAmount) : null}
        addOns={addOns}
        couponCode={flow.appliedCoupon?.code}
        discountAmount={flow.discountAmount}
      />
      </>)}

      {/* F-204: whoever pays declares the age to buy (18, or a legal guardian's authorization). */}
      {paying && flow.isAuthenticated && flow.ageDeclaration && (
        <div className="space-y-1" data-testid="checkout-age-declaration">
          <label htmlFor="checkout-age-declaration" className="flex items-start gap-2 text-sm text-foreground-secondary">
            <input
              id="checkout-age-declaration"
              type="checkbox"
              checked={flow.ageDeclaration.checked}
              onChange={(e) => flow.ageDeclaration?.onChange(e.target.checked)}
              disabled={busy}
              aria-invalid={flow.ageDeclaration.error}
              aria-describedby={flow.ageDeclaration.error ? 'checkout-age-declaration-error' : undefined}
              className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-border text-primary"
            />
            <span>{t('purchase.ageDeclaration', { age: PURCHASE_AGE })}</span>
          </label>
          {flow.ageDeclaration.error && (
            <p id="checkout-age-declaration-error" role="alert" className="text-sm text-danger-text">{t('purchase.errors.ageDeclarationRequired')}</p>
          )}
        </div>
      )}

      {/* The quote knows when a signed-in buyer already holds this plan: say so
          before the form is filled, since the API would refuse it (409). */}
      {held && !flow.submitError && (
        <div role="status" className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>
            {t('purchase.alreadySubscribed')}{' '}
            <button type="button" onClick={seeSubscription} className="font-semibold underline hover:no-underline">
              {t('purchase.seeSubscription')}
            </button>
          </span>
        </div>
      )}

      {refusal && !flow.submitError && (
        <div role="alert" className="flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>{refusal}</span>
        </div>
      )}

      {flow.submitError && (
        <div role="alert" className="flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>
            {flow.submitError.message || t(flow.submitError.key || 'purchase.errors.generic')}
            {flow.submitError.soldOut && (
              <JoinWaitlistButton productId={product.id} className="mt-2" />
            )}
            {flow.submitError.alreadySubscribed && (
              <>
                {' '}
                <button type="button" onClick={seeSubscription} className="font-semibold underline hover:no-underline">
                  {t('purchase.seeSubscription')}
                </button>
              </>
            )}
            {flow.submitError.needsPlan && (
              <>
                {' '}
                <button type="button" onClick={() => navigate('/pricing')} className="font-semibold underline hover:no-underline">
                  {t('purchase.seePlans')}
                </button>
              </>
            )}
          </span>
        </div>
      )}

      {notices}

      {flow.isAuthenticated ? (
        <Button
          type="submit"
          variant="secondary"
          size="lg"
          disabled={busy || cardBlocked || flow.quote.loading || held || Boolean(refusal) || submitDisabled}
          data-testid="checkout-submit"
          className="h-11 w-full text-base font-semibold"
        >
          {busy ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {busyLabel ?? t('processing')}
            </>
          ) : flow.isFree && !ownSummary ? t('products:freeClaim.cta') : submitLabel}
        </Button>
      ) : asksToJoin ? (
        <div className="space-y-2 text-center" data-testid="checkout-request-to-join">
          <Button type="button" variant="secondary" size="lg" className="h-11 w-full text-base font-semibold" onClick={openRequestToJoin}>
            <UserPlus className="mr-2 h-4 w-4" />
            {t('purchase.requestToJoin')}
          </Button>
          <p className="text-xs text-gray-500">{t('purchase.requestToJoinHint')}</p>
          <button type="button" onClick={openLogin} className="text-sm font-medium text-primary hover:underline">{t('purchase.alreadyMember')}</button>
        </div>
      ) : (
        <div className="space-y-2 text-center">
          <Button type="button" variant="secondary" size="lg" className="h-11 w-full text-base font-semibold" onClick={openLogin}>
            <LogIn className="mr-2 h-4 w-4" />
            {t('purchase.signInToContinue')}
          </Button>
          <p className="text-xs text-gray-500">{t('purchase.signInHint')}</p>
        </div>
      )}

      {paying && (
      <p className="flex items-center justify-center gap-1.5 text-xs text-gray-500">
        <Lock className="h-3.5 w-3.5" />
        {t('securePayment')}
      </p>
      )}
    </form>
  )
}
